import { SagaIterator } from 'redux-saga';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import * as reportService from '../../services/reportAPI';
import {
  IFetchAssessmentListRequest,
  IFetchIEnrolledPatientStateRequest,
  IFetchIScreenedListStateRequest,
  IFetchIUserPerformanceListStateRequest,
  IFetchSSReportViewRequest,
  IFetchSKReportViewRequest,
  IFetchSKDetailsListRequest,
  IFetchSKDetailsViewRequest
} from '../report/types';
import {
  fetchAssessmentListFailure,
  fetchAssessmentListSuccess,
  fetchEnrolledPatientFailure,
  fetchEnrolledPatientSuccess,
  fetchScreenedPatientListFailure,
  fetchScreenedPatientListSuccess,
  fetchSSReportViewFailure,
  fetchSSReportViewSuccess,
  fetchSKReportViewFailure,
  fetchSKReportViewSuccess,
  fetchSKDetailsListFailure,
  fetchSKDetailsListSuccess,
  fetchSKDetailsViewFailure,
  fetchSKDetailsViewSuccess,
  fetchUserPerformanceListFailure,
  fetchUserPerformanceListSuccess
} from './actions';
import {
  FETCH_ASSESSMENT_LIST_REQUEST,
  FETCH_ENROLLED_PATIENT_REQUEST,
  FETCH_SCREENED_PATIENT_LIST_REQUEST,
  FETCH_SS_REPORT_VIEW_REQUEST,
  FETCH_SK_REPORT_VIEW_REQUEST,
  FETCH_SK_DETAILS_LIST_REQUEST,
  FETCH_SK_DETAILS_VIEW_REQUEST,
  FETCH_USER_PERFORMANCE_LIST_REQUEST
} from './actionTypes';

/*
  Worker Saga: Fired on FETCH_SCREENED_PATIENT_LIST_REQUEST action
*/
export function* fetchScreenedList(action: IFetchIScreenedListStateRequest): SagaIterator {
  try {
    const {
      data: { entityList: screenedList, totalCount, limit }
    } = yield call(reportService.fetchScreenedReport, action.payload);
    const payload = { screenedList, totalCount, limit };
    yield put(fetchScreenedPatientListSuccess(payload));
  } catch (e) {
    if (e instanceof Error) {
      action.payload.failureCb?.(e);
      yield put(fetchScreenedPatientListFailure(e));
    }
  }
}

/*
  Worker Saga: Fired on FETCH_ENROLLED_PATIENT_REQUEST action
*/
export function* fetchEnrolledPatientList(action: IFetchIEnrolledPatientStateRequest): SagaIterator {
  try {
    const {
      data: { entityList: enrolledPatientList, totalCount, limit }
    } = yield call(reportService.fetchEnrolledPatientReport, action.payload);
    const payload = { enrolledPatientList, totalCount, limit };
    yield put(fetchEnrolledPatientSuccess(payload));
  } catch (e) {
    if (e instanceof Error) {
      action.payload.failureCb?.(e);
      yield put(fetchEnrolledPatientFailure(e));
    }
  }
}
/*
  Worker Saga: Fired on FETCH_ASSESSMENT_LIST_REQUEST action
*/
export function* fetchAssessmentList(action: IFetchAssessmentListRequest): SagaIterator {
  try {
    const {
      data: { entityList: assessmentList, totalCount, limit }
    } = yield call(reportService.fetchAssessmentListReport, action.payload);
    const payload = { assessmentList, totalCount, limit };
    yield put(fetchAssessmentListSuccess(payload));
  } catch (e) {
    if (e instanceof Error) {
      action.payload.failureCb?.(e);
      yield put(fetchAssessmentListFailure(e));
    }
  }
}

/*
  Worker Saga: Fired on FETCH_USER_PERFORMANCE_LIST_REQUEST action
*/
function* fetchUserPerformanceList(action: IFetchIUserPerformanceListStateRequest): SagaIterator {
  try {
    const {
      data: { entityList: userPerformanceList, totalCount, limit }
    } = yield call(reportService.fetchUserPerformanceReport, action.payload);
    const payload = { userPerformanceList, totalCount, limit };
    yield put(fetchUserPerformanceListSuccess(payload));
  } catch (e) {
    if (e instanceof Error) {
      action.payload.failureCb?.(e);
      yield put(fetchUserPerformanceListFailure(e));
    }
  }
}

/*
  Starts worker saga on latest dispatched specific action.
  Allows concurrent increments.
*/
export function* fetchSSReportViewSaga(action: IFetchSSReportViewRequest): SagaIterator {
  try {
    const {
      data: { entityList = [], totalCount = 0 }
    } = yield call(reportService.fetchSSReportView, action.payload);
    yield put(fetchSSReportViewSuccess({ list: entityList, totalCount }));
  } catch (e) {
    if (e instanceof Error) {
      action.failureCb?.(e);
      yield put(fetchSSReportViewFailure(e));
    }
  }
}

export function* fetchSKReportViewSaga(action: IFetchSKReportViewRequest): SagaIterator {
  try {
    const {
      data: { entityList = [], totalCount = 0 }
    } = yield call(reportService.fetchSKReportView, action.payload);
    yield put(fetchSKReportViewSuccess({ list: entityList, totalCount }));
  } catch (e) {
    if (e instanceof Error) {
      action.failureCb?.(e);
      yield put(fetchSKReportViewFailure(e));
    }
  }
}

export function* fetchSKDetailsListSaga(action: IFetchSKDetailsListRequest): SagaIterator {
  try {
    const {
      data: { entityList = [], totalCount = 0 }
    } = yield call(reportService.fetchSKDetailsList, action.payload);
    yield put(fetchSKDetailsListSuccess({ list: entityList, totalCount }));
  } catch (e) {
    if (e instanceof Error) {
      action.failureCb?.(e);
      yield put(fetchSKDetailsListFailure(e));
    }
  }
}

export function* fetchSKDetailsViewSaga(action: IFetchSKDetailsViewRequest): SagaIterator {
  try {
    const {
      data: { entity }
    } = yield call(reportService.fetchSKDetailsView, action.payload);
    if (!entity) {
      throw new Error('SK details entity missing from response');
    }
    yield put(fetchSKDetailsViewSuccess(entity));
  } catch (e) {
    if (e instanceof Error) {
      action.failureCb?.(e);
      yield put(fetchSKDetailsViewFailure(e));
    }
  }
}

function* reportSaga() {
  yield all([takeLatest(FETCH_SCREENED_PATIENT_LIST_REQUEST, fetchScreenedList)]);
  yield all([takeLatest(FETCH_ENROLLED_PATIENT_REQUEST, fetchEnrolledPatientList)]);
  yield all([takeLatest(FETCH_ASSESSMENT_LIST_REQUEST, fetchAssessmentList)]);
  yield all([takeLatest(FETCH_USER_PERFORMANCE_LIST_REQUEST, fetchUserPerformanceList)]);
  yield all([takeLatest(FETCH_SS_REPORT_VIEW_REQUEST, fetchSSReportViewSaga)]);
  yield all([takeLatest(FETCH_SK_REPORT_VIEW_REQUEST, fetchSKReportViewSaga)]);
  yield all([takeLatest(FETCH_SK_DETAILS_LIST_REQUEST, fetchSKDetailsListSaga)]);
  yield all([takeLatest(FETCH_SK_DETAILS_VIEW_REQUEST, fetchSKDetailsViewSaga)]);
}

export default reportSaga;
